import { breakpoints, Card, Column, Columns, PageBlock, vars } from '@etg/wings';
import { ContentWrapper, LoadingDots } from '@eti/components';
import { css } from '@eti/styles';
import { useMutation } from '@apollo/client';
import type { ExtraProductSelectionInput } from '@eti/schema-types';
import { useHistory } from 'react-router-dom';
import {
  ADD_TO_ADDON_CART,
  EMPTY_ADDON_CART_MUTATION,
} from '../../../post-booking/ancillary-store/entry-page/containers/mutations';
import { routes } from '../../../../constants/routesAndApisConstants';
import type { OrderTypePostbooking } from '../../../post-booking/ancillary-store/utils/OrderUtils';
import ExtraProductsPageContainer from '../../../post-booking/ancillary-store/entry-page/containers/ExtraProductsPageContainer';
import { useOrderDetails } from '../../containers/OrderDetailsContext';
import { tripTypes } from '../../../../constants/tripTypesConstants';
import ErrorPage from '../../../status-pages/components/ErrorPage';
import { useRoutedTabs } from '../../../../widgets/tabs/components/RoutedTabs';
import { getSelectedAncillaryBundleOptions } from './utils';

const formStyles = css`
  margin-top: 25px;

  @media (min-width: ${breakpoints._970}) {
    margin-top: 35px;
  }
`;

const loadingStyles = css`
  color: ${vars.colors.links.default};
  display: flex;
  font-size: 1.5rem;
  justify-content: center;
  padding: 24px;
`;

const OrderDetailsExtraProducts = () => {
  const history = useHistory();
  const {
    addOnOrders,
    availableExtraProducts,
    ancillaryAvailabilityLoading,
    ancillaryBundles,
    bounds,
    customerDetails,
    extraProducts,
    journeyType,
    travelers,
    tripId,
  } = useOrderDetails();
  const { tabHeading1Ref } = useRoutedTabs();
  const [addToAddonCart, { loading: addToAddonCartLoading }] = useMutation(ADD_TO_ADDON_CART);
  const [emptyCart, { loading: emptyCartLoading }] = useMutation(EMPTY_ADDON_CART_MUTATION);

  if (ancillaryAvailabilityLoading) {
    return (
      <PageBlock withPaddingY>
        <ContentWrapper>
          <Columns>
            <Column width={{ [breakpoints._0]: '4/4', [breakpoints._970]: '4/6' }}>
              <Card>
                <LoadingDots
                  containerStyle={loadingStyles}
                  dataTestId="order-details-extra-products-loading"
                />
              </Card>
            </Column>
          </Columns>
        </ContentWrapper>
      </PageBlock>
    );
  }

  if (!availableExtraProducts) {
    return <ErrorPage />;
  }

  const addOnOrderProducts = addOnOrders.flatMap((productOrder) => productOrder.extraProducts);

  const onAddToAddonCart = async (selection: ExtraProductSelectionInput[]) => {
    try {
      await emptyCart();
      await addToAddonCart({
        variables: {
          tripId,
          extraProductSelection: selection,
        },
      });
      history.push(routes.PAYMENT_LINK);
    } catch (e) {
      console.error(e); // eslint-disable-line no-console
    }
  };

  const order = {
    availableExtraProducts,
    bounds,
    extraProducts: [...extraProducts, ...addOnOrderProducts],
    travelers,
    tripId,
    isMultiStop: journeyType === tripTypes.MULTI_STOP,
    addedAncillaryBundles: getSelectedAncillaryBundleOptions(ancillaryBundles),
    tripType: journeyType,
  } as OrderTypePostbooking;

  return (
    <ContentWrapper className={formStyles}>
      <ExtraProductsPageContainer
        contactEmail={customerDetails.email}
        isAddToAddonCartLoading={addToAddonCartLoading || emptyCartLoading}
        onAddToAddonCart={onAddToAddonCart}
        order={order}
        tabHeading1Ref={tabHeading1Ref}
      />
    </ContentWrapper>
  );
};

export default OrderDetailsExtraProducts;
